import { createFileRoute } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PrimaryButton } from "@/components/brand/PrimaryButton";

export const Route = createFileRoute("/admin/activity")({
  head: () => ({
    meta: [
      { title: "Activity Logs — Command Center" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Activity Logs" nav={ADMIN_NAV}>
        <ActivityLogs />
      </PortalShell>
    </ProtectedRoute>
  ),
});

interface LogRow {
  id: string;
  created_at: string;
  user_id: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  ip_address: string | null;
  actor_name: string;
  actor_role: string;
}

const PAGE = 50;

const ROLE_PILL: Record<string, string> = {
  super_admin: "bg-navy-800 text-cream-50",
  admin: "bg-navy-700 text-cream-50",
  agent: "bg-bronze-600 text-navy-900",
  client: "bg-bronze-400/30 text-navy-800",
};

function ActivityLogs() {
  const [rows, setRows] = useState<LogRow[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [actorFilter, setActorFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => { void load(); }, [page, actionFilter, dateFrom, dateTo]);

  async function load() {
    let q = supabase
      .from("activity_logs")
      .select("id, created_at, user_id, action, target_type, target_id, ip_address")
      .order("created_at", { ascending: false })
      .range(page * PAGE, page * PAGE + PAGE);

    if (actionFilter !== "all") q = q.like("action", `${actionFilter}.%`);
    if (dateFrom) q = q.gte("created_at", new Date(dateFrom).toISOString());
    if (dateTo) q = q.lte("created_at", new Date(dateTo + "T23:59:59").toISOString());

    const { data, error } = await q;
    if (error) return;
    const raw = (data ?? []) as Array<Omit<LogRow, "actor_name" | "actor_role">>;
    setHasMore(raw.length > PAGE);
    const trimmed = raw.slice(0, PAGE);

    const userIds = Array.from(new Set(trimmed.map((r) => r.user_id).filter(Boolean) as string[]));
    const [profilesRes, rolesRes] = await Promise.all([
      userIds.length ? supabase.from("profiles").select("id, full_name").in("id", userIds) : Promise.resolve({ data: [] as Array<{ id: string; full_name: string | null }> }),
      userIds.length ? supabase.from("user_roles").select("user_id, role").in("user_id", userIds) : Promise.resolve({ data: [] as Array<{ user_id: string; role: string }> }),
    ]);

    const nameMap = new Map((profilesRes.data ?? []).map((p) => [p.id, p.full_name || "—"]));
    const roleMap = new Map<string, string>();
    for (const r of (rolesRes.data ?? []) as Array<{ user_id: string; role: string }>) {
      const prev = roleMap.get(r.user_id);
      const rank: Record<string, number> = { super_admin: 4, admin: 3, agent: 2, client: 1 };
      if (!prev || (rank[r.role] ?? 0) > (rank[prev] ?? 0)) roleMap.set(r.user_id, r.role);
    }

    setRows(trimmed.map((r) => ({
      ...r,
      actor_name: r.user_id ? (nameMap.get(r.user_id) || "—") : "System",
      actor_role: r.user_id ? (roleMap.get(r.user_id) || "user") : "system",
    })));
  }

  const visible = useMemo(
    () => rows.filter((r) => !actorFilter || r.actor_name.toLowerCase().includes(actorFilter.toLowerCase())),
    [rows, actorFilter],
  );

  function exportCsv() {
    const header = ["Timestamp", "Actor", "Role", "Action", "Target Type", "Target", "IP"];
    const lines = visible.map((r) => [
      new Date(r.created_at).toISOString(),
      r.actor_name, r.actor_role, r.action, r.target_type ?? "", r.target_id ?? "", r.ip_address ?? "",
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `activity-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-end justify-between gap-4">
        <h2 className="font-display font-semibold text-navy-800" style={{ fontSize: "32px", letterSpacing: "-0.01em" }}>
          Activity <em className="text-bronze-600 italic font-normal">Logs</em>
        </h2>
        <PrimaryButton onClick={exportCsv}>Export CSV</PrimaryButton>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Input
          value={actorFilter} onChange={(e) => setActorFilter(e.target.value)}
          placeholder="Filter by actor…" className="w-56 bg-white border-bronze-600/30"
        />
        <Select value={actionFilter} onValueChange={(v) => { setPage(0); setActionFilter(v); }}>
          <SelectTrigger className="w-48 bg-white border-bronze-600/30"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="auth">Auth</SelectItem>
            <SelectItem value="client">Client</SelectItem>
            <SelectItem value="document">Document</SelectItem>
            <SelectItem value="lead">Lead</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => { setPage(0); setDateFrom(e.target.value); }} className="w-40 bg-white border-bronze-600/30" />
        <Input type="date" value={dateTo} onChange={(e) => { setPage(0); setDateTo(e.target.value); }} className="w-40 bg-white border-bronze-600/30" />
      </div>

      <div className="mt-6 bg-white border border-bronze-600/20 rounded-[4px] shadow-soft overflow-hidden">
        <table className="w-full">
          <thead className="bg-cream-50 border-b border-bronze-600/20">
            <tr className="text-left font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
              <th className="px-4 py-3">Timestamp</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Target</th>
              <th className="px-4 py-3">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-bronze-600/15">
            {visible.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center font-sans text-[13px] text-charcoal-500">No activity recorded.</td></tr>
            )}
            {visible.map((r) => (
              <tr key={r.id} className="hover:bg-cream-50/60">
                <td className="px-4 py-3 font-sans text-[12px] text-charcoal-500">
                  {new Date(r.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-sans text-[13px] text-charcoal-900">{r.actor_name}</span>
                    <span className={`px-2 py-0.5 rounded-full font-sans text-[9px] uppercase tracking-[0.12em] ${ROLE_PILL[r.actor_role] ?? "bg-charcoal-500/15 text-charcoal-500"}`}>
                      {r.actor_role}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 font-sans text-[12px] text-charcoal-900">
                  {r.action.replace(/[._]/g, " ")}
                </td>
                <td className="px-4 py-3 font-sans text-[12px] text-charcoal-500">
                  {r.target_type ? `${r.target_type} · ${r.target_id?.slice(0, 8) ?? ""}` : "—"}
                </td>
                <td className="px-4 py-3 font-sans text-[11px] text-charcoal-500">{r.ip_address ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="font-sans text-[12px] text-charcoal-500">Page {page + 1}</p>
        <div className="flex gap-2">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1.5 border border-bronze-600/30 font-sans text-[12px] text-charcoal-900 disabled:opacity-40 rounded-[2px]"
          >
            ← Previous
          </button>
          <button
            disabled={!hasMore}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 border border-bronze-600/30 font-sans text-[12px] text-charcoal-900 disabled:opacity-40 rounded-[2px]"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}